import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { UiFlowKey } from "@signalist/shared-types";

import { faPuzzlePiece } from "@fortawesome/pro-regular-svg-icons";

import { getExtensionStoreUrl, isEdgeBrowser } from "@/utils/extensionStore";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";

import { useAppSelector } from "@/redux/hooks";

import {
	useGetSeenUiFlowsQuery,
	useMarkUiFlowSeenMutation,
} from "@/pages/Private/redux/userUiFlow/userUiFlow.api";
import { userUiFlowSelector } from "@/pages/Private/redux/userUiFlow/userUiFlow.slice";

import { DashboardBox } from "./DashboardBox";

interface ExtensionPromoBoxProps {
	/** The user already installed the browser extension — nothing left to promote. */
	installed: boolean;
}

/**
 * Dashboard card promoting the Signalist browser extension (PREC-3815). Hidden
 * once the extension has been installed, and dismissible for good via the
 * `user_ui_flow` marker. The install CTA opens the store matching the user's
 * browser (Edge Add-ons for Edge, Chrome Web Store otherwise).
 */
export const ExtensionPromoBox: FunctionComponent<ExtensionPromoBoxProps> = ({ installed }) => {
	const { t } = useTranslation();
	// Reuses the tour EXTENSION step's copy — same pitch, and `en.ts`/`de.ts` are
	// length-ratcheted, so the promo strings live in the onboarding-tour modules.
	const ts = (key: string) => t(`onboardingTour.steps.extension.${key}`);

	useGetSeenUiFlowsQuery();
	const { seenKeys, loaded } = useAppSelector(userUiFlowSelector);
	const [markUiFlowSeen] = useMarkUiFlowSeenMutation();
	// Rollout gate (currently company 1 only) — undefined while loading counts as off.
	const promoEnabled = useIsFeatureActive(FeaturesCodes.EXTENSION_PROMOTION) === true;

	if (
		!promoEnabled ||
		installed ||
		!loaded ||
		seenKeys.includes(UiFlowKey.EXTENSION_PROMO_DASHBOARD)
	) {
		return null;
	}

	const installLabelKey = isEdgeBrowser(navigator.userAgent) ? "installEdge" : "installChrome";

	const openStore = () => {
		window.open(getExtensionStoreUrl(), "_blank", "noopener,noreferrer");
	};

	return (
		<DashboardBox
			accent="purple"
			icon={<Icon className="w-4 h-4" icon={faPuzzlePiece} />}
			subtitle={ts("desc")}
			title={ts("title")}
			onDismiss={() => markUiFlowSeen(UiFlowKey.EXTENSION_PROMO_DASHBOARD)}
		>
			<div className="flex flex-col gap-3 px-4 py-3">
				<p className="text-ssm text-text-meta">{ts("intro")}</p>
				<Button
					className="w-fit"
					size={ButtonSize.S}
					title={ts(installLabelKey)}
					type="button"
					onClick={openStore}
				/>
			</div>
		</DashboardBox>
	);
};
